import router from "@/router";
import { firebase } from "@/firebase/firebaseConfig";
import { AiseaApiFactory } from "@/api/aisea/apiFactory";
import AuthService from "@/services/authService";
import LocalDbService from "@/services/localDbService";
import secureDataflowService from "@/services/secureDataflowService";
import operationLocalDbService from "@/services/operations/operationLocalDbService";
import FireStoreService from "@/services/fireStoreService";
import Vue from "vue";
import Store from "@/store/store";
import { broadcastUser, broadcastLogout } from "@/services/userSyncService";

export default {
  async loginWithEnterprise({ dispatch }, payload) {
    // device_uuid ヘッダーに追加
    const uuid = await AuthService.getDeviceUuid({
      enterpriseId: payload.loginInfo.enterprise_id,
      userId: payload.loginInfo.id,
    });
    const header = uuid ? { device_uuid: uuid } : {};

    const accountRes = await AiseaApiFactory.get("account").loginWithEnterprise(
      payload.loginInfo,
      header
    );

    if (accountRes.status && accountRes.data.mfa_required) {
      // 法人ログイン認証:成功 かつ MFA必須 の場合は、MFA実施
      await AuthService.saveMfa({
        enterpriseId: payload.loginInfo.enterprise_id,
        accountId: accountRes.data.account_id,
        userId: payload.loginInfo.id,
        mfaToken: accountRes.data.mfa_token,
        message: accountRes.data.mfa_message,
        rememberIds: payload.rememberIds,
      });
      router.push({ name: "mfa" });
      return null;
    }
    if (!accountRes.status) {
      // 法人ログイン認証:失敗
      throw { data: accountRes.data, statusCode: accountRes.code };
    }

    return await dispatch("handleEnterpriseLoginSuccess", {
      accountRes,
      payload,
    });
  },

  async mfaWithEnterprise({ dispatch }, payload) {
    const accountRes = await AiseaApiFactory.get("account").mfaWithEnterprise({
      account_id: payload.loginInfo.account_id,
      enterprise_id: payload.loginInfo.enterprise_id,
      mfa_code: payload.loginInfo.mfa_code,
      mfa_token: payload.loginInfo.mfa_token,
    });

    if (!accountRes.status) {
      // 2段階認証:失敗
      throw { data: accountRes.data, statusCode: accountRes.code };
    }

    return await dispatch("handleEnterpriseLoginSuccess", {
      accountRes,
      payload,
    });
  },

  async handleEnterpriseLoginSuccess({ commit }, { accountRes, payload }) {
    // >>> auth-trace (検証用、後で `>>> auth-trace` ～ `<<< auth-trace` を含む全ブロックを削除)
    Vue.$log.info("[auth-trace] handleEnterpriseLoginSuccess: start", {
      ts: Date.now(),
    });
    // <<< auth-trace
    commit("SET_LOGIN_SUCCESS");

    // 早期 broadcastUser: signInWithCustomToken の前に localStorage['user'] へ最低限の情報を書く。
    // Slow 3G で signInWithCustomToken → 完全版 broadcast まで 2〜3 秒空く窓の間に、
    // 別タブの aisea-frontend-shared (use-auth.tsx) が Firebase user あり + cachedUser なしと
    // 判定して tokenManager.signOut() を呼び、全タブで Firebase セッションを落とす race を防ぐ。
    // enterpriseName / loginKey は enterprise.fetch() 後に完全版で上書きする。
    broadcastUser({
      accountId: accountRes.data.account_id,
      enterpriseId: accountRes.data.enterprise_id,
      enterpriseName: undefined,
      loginKey: accountRes.data.login_key,
      isDebug: accountRes.data.is_debug === true,
      isFree: accountRes.data.is_free === true,
      addons: accountRes.data.addons,
    });

    try {
      await firebase
        .auth()
        .setPersistence(firebase.auth.Auth.Persistence.LOCAL);

      const fbRes = await firebase
        .auth()
        .signInWithCustomToken(accountRes.data.custom_token);
      // >>> auth-trace
      Vue.$log.info("[auth-trace] signInWithCustomToken done", {
        uid: fbRes.user.uid,
        ts: Date.now(),
      });
      // <<< auth-trace

      let idToken = await fbRes.user.getIdToken();
      let refreshToken = fbRes.user.refreshToken;
      commit("SET_ID_TOKEN", { idToken: idToken });
      await AuthService.saveTokens({ idToken, refreshToken });

      // FireStoreの auth_settings書き込み状況から、初回ログインか判断
      const authSettingsOnFirestore = await FireStoreService.getAccountSetting(
        "auth_settings"
      );
      const isFirstLogin =
        !authSettingsOnFirestore ||
        !Object.prototype.hasOwnProperty.call(
          authSettingsOnFirestore,
          "is_free"
        );

      const userRes = await AiseaApiFactory.get("enterprise").fetch();
      let user = userRes.data;
      user.account_id = payload.loginInfo.id;
      user.is_first_login = isFirstLogin;
      commit("SET_USER", { user: user });

      let ids = {
        userId: null,
        enterpriseId: null,
      };

      if (payload.rememberIds) {
        ids.userId = payload.loginInfo.id;
        ids.enterpriseId = payload.loginInfo.enterprise_id;
      }

      let addons = accountRes.data.addons;
      Store.commit("SET_ADDONS", { addons: addons });

      // 有料・無料情報
      const isFree = accountRes.data.is_free;
      Store.commit("SET_IS_FREE", { isFree });

      FireStoreService.setAccountSetting("auth_settings", {
        is_free: isFree,
      });

      await AuthService.saveAuthInfo({
        rememberIds: ids,
        idToken: idToken,
        refreshToken: refreshToken,
        addons: accountRes.data.addons,
        user: user,
        isFree,
      });

      // ログイン成功したら、enterpriseNameを付与して、BroadcastChannel で他サービス（operation 等）に UserInfo を通知
      broadcastUser({
        accountId: fbRes.user.uid,
        enterpriseId: user.id,
        enterpriseName: user.name,
        loginKey: user.login_key,
        isDebug: accountRes.data.is_debug === true,
        isFree: isFree === true,
        addons: accountRes.data.addons,
      });

      if (accountRes.data.device_uuid) {
        await AuthService.saveDeviceUuid({
          enterpriseId: payload.loginInfo.enterprise_id,
          userId: payload.loginInfo.id,
          uuid: accountRes.data.device_uuid,
        });
      }
      await AuthService.clearMfa();

      // FireStoreからユーザー設定を取得
      await FireStoreService.setEnterpriseAccountSettingsToLocalDb();

      Vue.$log.info("====== login Aisea ======");
      // >>> auth-trace
      Vue.$log.info(
        "[auth-trace] handleEnterpriseLoginSuccess: pushing live-map",
        {
          currentUser: !!firebase.auth().currentUser,
          ts: Date.now(),
        }
      );
      // <<< auth-trace
      router.push({ name: "enterprise-live-map" });

      const enterpriseAccountSettings = await FireStoreService.getEnterpriseAccountSettings();
      if (enterpriseAccountSettings["port_master_read_only"] === true) {
        // PortMaster読み取り専用の設定がされているアカウントは、トップはPortMasterメニュー
        router.push({ name: "addon-port-master-port-menu" });
      }

      operationLocalDbService.deleteAllRecords(); // ログイン時に operation のマスタデータ削除

      return fbRes;
    } catch (err) {
      // 早期 broadcastUser 後にログイン処理が失敗した場合、broadcastLogout で localStorage['user'] をクリアして再 throw する。
      // >>> auth-trace
      Vue.$log.warn(
        "[auth-trace] handleEnterpriseLoginSuccess: failed after early broadcast → broadcastLogout",
        {
          ts: Date.now(),
          message: err && err.message,
        }
      );
      // <<< auth-trace
      try {
        broadcastLogout();
      } catch (_) {
        // 無視
      }
      throw err;
    }
  },

  async setAccountId({ commit }) {
    const accountId = await FireStoreService.getFirebaseAccountId();
    commit("SET_ACCOUNT_ID", { accountId });
  },

  async resetState({ commit }) {
    commit("RESET_STATE");
  },

  async logout({ dispatch }) {
    Vue.$log.info("====== logout Aisea ======");
    // >>> auth-trace
    Vue.$log.info("[auth-trace] logout: start", {
      ts: Date.now(),
    });
    // <<< auth-trace
    const cleanupSuccess = await dispatch("cleanupIndexedDbData").then(
      result => !result.some(({ status }) => status !== "fulfilled")
    );
    if (!cleanupSuccess) {
      throw new Error("Failed to cleanup local data");
    }
    await AiseaApiFactory.get("account").logout();
    // 他サービス（operation 等）にログアウトを通知し、localStorage の UserInfo を削除
    broadcastLogout();
    // signOut が後追いで次のログインに刺さらないよう、router.push より前に直列で完了させる
    await FireStoreService.cleanupLocalData();
    await dispatch("resetState"); // api リクエストによるログアウトが成功したら、state をリセット
    // >>> auth-trace
    Vue.$log.info("[auth-trace] logout: cleanup done, pushing login", {
      ts: Date.now(),
    });
    // <<< auth-trace
    router.push({
      name: "login-enterprise",
      query: { logout: "force" },
    });
  },

  mfaResend(_, payload) {
    return new Promise((resolve, reject) => {
      AiseaApiFactory.get("account")
        .mfaResend(payload.resendInfo)
        .then(res => {
          if (!res.status) return reject(res);
          return resolve(res);
        })
        .catch(reject);
    });
  },

  async cleanupIndexedDbData() {
    const result = await Promise.allSettled([
      LocalDbService.cleanup(),
      secureDataflowService.cleanup(),
      operationLocalDbService.deleteAllRecords(), // ログアウト時に operation のマスタデータ削除
    ]);
    return result;
  },

  // indexedDB、stateの不要なデータを削除する
  async cleanupLocalData({ dispatch }) {
    try {
      await Promise.all([
        dispatch("cleanupIndexedDbData"),
        FireStoreService.cleanupLocalData(),
      ]);
      await dispatch("resetState");
      return true;
    } catch {
      return false;
    }
  },
};
